import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO, startOfWeek, endOfWeek, isSameDay, startOfMonth, endOfMonth, isToday, addMinutes } from 'date-fns';
import { Calendar as CalendarIcon, Clock, Plus, Edit2, Trash2, AlertTriangle, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { useLocation, Link } from 'wouter';

import { bookingService } from '@/services/api/booking.service';
import { adminService } from '@/services/api/admin.service';
import { organizationService } from '@/services/api/organization.service';
import { generateTimeIntervals } from '@/utils/time';
import { Calendar } from '@/components/calendar/Calendar';
import type { CalendarEvent, EventStatus, CalendarView } from '@/lib/calendar/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import type { AvailableSlot, Service, Provider } from '@/types/booking';
import apiClient from '../../lib/api-client';
import { sanitizeErrorMessage } from '@/lib/error-utils';
import { useSetPageHeader } from '@/components/chrome/PageHeader';

// Enhanced validation schema factory with translation support
const createSlotSchema = (t: (key: string, options?: any) => string) => z.object({
  providerId: z.number().min(1, t('provider.required', { ns: 'validation' })),
  serviceId: z.number().min(1, t('service.required', { ns: 'validation' })),
  date: z.string().min(1, t('date.required', { ns: 'validation' }))
    .refine(date => {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, t('date.noPast', { ns: 'validation' })),
  startTime: z.string().min(1, t('startTime.required', { ns: 'validation' }))
    .regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, t('startTime.invalidFormat', { ns: 'validation' })),
  duration: z.number()
    .min(15, t('duration.min', { ns: 'validation', min: 15 }))
    .max(240, t('duration.max', { ns: 'validation', max: 240 })),
  maxCapacity: z.number().min(1).max(100).default(1),
  recurring: z.boolean().default(false),
  recurringDays: z.array(z.number()).optional(),
  endDate: z.string().optional(),
  generateMultiple: z.boolean().default(false),
  endTime: z.string().optional(),
}).refine(data => {
  // If recurring is true, ensure recurringDays is not empty
  if (data.recurring) {
    return data.recurringDays && data.recurringDays.length > 0;
  }
  return true;
}, {
  message: t('recurring.selectDays', { ns: 'validation' }),
  path: ['recurringDays'],
}).refine(data => {
  // If recurring is true, ensure endDate is provided and valid
  if (data.recurring) {
    if (!data.endDate) return false;
    const end = new Date(data.endDate);
    const start = new Date(data.date);
    return end > start;
  }
  return true;
}, {
  message: t('recurring.endDateAfterStart', { ns: 'validation' }),
  path: ['endDate'],
}).refine(data => {
  // If generateMultiple is true, ensure endTime is provided and valid
  if (data.generateMultiple) {
    if (!data.endTime) return false;
    // Validate endTime format
    if (!/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(data.endTime)) return false;
    // Ensure endTime is after startTime
    const [startHour, startMin] = data.startTime.split(':').map(Number);
    const [endHour, endMin] = data.endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes > startMinutes;
  }
  return true;
}, {
  message: t('timeRange.endTimeRequired', { ns: 'validation' }),
  path: ['endTime'],
});

type SlotFormData = z.infer<ReturnType<typeof createSlotSchema>>;

// Type for field-specific errors from backend
interface FieldErrors {
  [key: string]: string[];
}

// Type for conflict detection
interface SlotConflict {
  id: number;
  startTime: string;
  endTime: string;
  service?: { name: string };
}

export default function AdminSlotsPage() {
  const { t } = useTranslation(['timeSlots', 'common', 'validation', 'bookings']);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month' | 'list'>('week');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [createTab, setCreateTab] = useState<'single' | 'recurring' | 'timerange'>('single');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);
  const [conflictingSlots, setConflictingSlots] = useState<SlotConflict[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayTimezone, setDisplayTimezone] = useState<string>('local');

  const { toast } = useToast();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  // Create schema with translation function
  const slotSchema = useMemo(() => createSlotSchema(t), [t]);

  // Fetch organization data for business hours
  const { data: organization, isLoading: isLoadingOrg } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });

  useSetPageHeader(
    {
      title: t('timeSlots:title'),
      sub: t('timeSlots:subtitle'),
      actions: (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <div className="hidden md:flex items-center gap-2">
            <span className="text-body-sm text-soft">Timezone:</span>
            <Select value={displayTimezone} onValueChange={setDisplayTimezone}>
              <SelectTrigger className="w-[200px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="local">
                  Browser Local ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                </SelectItem>
                <SelectItem value={organization?.timezone || 'UTC'}>
                  Organization ({organization?.timezone || 'UTC'})
                </SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            data-testid="create-slot-btn"
            size="md"
            onClick={() => setIsCreateDialogOpen(true)}
            iconLeft={<Plus className="h-4 w-4" />}
          >
            {t('timeSlots:createSlot')}
          </Button>
        </div>
      ),
    },
    [t, displayTimezone, organization?.timezone],
  );

  const form = useForm<SlotFormData>({
    resolver: zodResolver(slotSchema),
    mode: 'onSubmit', // Validate on submit to avoid premature errors
    defaultValues: {
      providerId: 0,
      serviceId: 0,
      date: format(selectedDate, 'yyyy-MM-dd'),
      startTime: '09:00',
      duration: 60,
      maxCapacity: 1,
      recurring: false,
      recurringDays: [],
      endDate: '',
      generateMultiple: false,
      endTime: '',
    },
  });

  // Watch form values for real-time conflict checking
  const watchedProviderId = form.watch('providerId');
  const watchedDate = form.watch('date');
  const watchedStartTime = form.watch('startTime');
  const watchedDuration = form.watch('duration');

  // Calculate week start from selected date
  const weekStart = useMemo(() => startOfWeek(selectedDate, { weekStartsOn: 1 }), [selectedDate]);

  // Default business hours fallback (9 AM - 5 PM)
  // Generate time intervals for the selected date
  const getTimeIntervalsForDate = (date: Date) => {
    const intervals = generateTimeIntervals(15, false);

    // Filter out past times when today is selected
    if (isToday(date)) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTotalMinutes = currentHour * 60 + currentMinute;

      return intervals.filter(interval => {
        const [hour, minute] = interval.value.split(':').map(Number);
        const intervalTotalMinutes = hour * 60 + minute;
        return intervalTotalMinutes > currentTotalMinutes;
      });
    }

    return intervals;
  };

  // Smart defaults and helpers
  // Generate 15-minute time intervals with 12-hour format (AM/PM)
  const timeIntervals = generateTimeIntervals(15, false);
  const timeSlots = timeIntervals;
  const weekDays = [
    { label: t('timeSlots:days.mon'), dayId: 1 },
    { label: t('timeSlots:days.tue'), dayId: 2 },
    { label: t('timeSlots:days.wed'), dayId: 3 },
    { label: t('timeSlots:days.thu'), dayId: 4 },
    { label: t('timeSlots:days.fri'), dayId: 5 },
    { label: t('timeSlots:days.sat'), dayId: 6 },
    { label: t('timeSlots:days.sun'), dayId: 0 },
  ];

  // Check for conflicts when provider, date, or time changes
  useEffect(() => {
    const checkForConflicts = async () => {
      if (watchedProviderId > 0 && watchedDate && watchedStartTime && watchedDuration > 0) {
        setIsCheckingConflict(true);
        try {
          // Calculate the proposed slot time range
          const proposedStart = new Date(`${watchedDate}T${watchedStartTime}:00`);
          const proposedEnd = addMinutes(proposedStart, watchedDuration);

          // Fetch provider's existing slots for the selected date
          const response = await apiClient.get(
            `/v1/admin/available_slots?provider_id=${watchedProviderId}&date=${watchedDate}`
          );

          const existingSlots = response.data.data?.slots || [];

          // Check for conflicts
          const conflicts = existingSlots.filter((slot: AvailableSlot) => {
            // Skip the current slot if we're editing
            if (selectedSlot && slot.id === selectedSlot.id) return false;

            const slotStart = new Date(slot.start_time || slot.startTime || '');
            const slotEnd = new Date(slot.end_time || slot.endTime || '');

            // Check for overlap
            return (
              (proposedStart < slotEnd && proposedEnd > slotStart) ||
              (slotStart < proposedEnd && slotEnd > proposedStart)
            );
          });

          setConflictingSlots(conflicts);
        } catch (error) {
          console.error('Error checking for conflicts:', error);
        } finally {
          setIsCheckingConflict(false);
        }
      } else {
        setConflictingSlots([]);
      }
    };

    // Debounce the conflict check
    const timeoutId = setTimeout(checkForConflicts, 500);
    return () => clearTimeout(timeoutId);
  }, [watchedProviderId, watchedDate, watchedStartTime, watchedDuration, selectedSlot]);

  // Fetch available slots for the selected date range
  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'month') {
      // For month view, fetch the entire month plus surrounding weeks
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      return {
        start: startOfWeek(monthStart, { weekStartsOn: 1 }),  // Include days from previous month
        end: endOfWeek(monthEnd, { weekStartsOn: 1 })         // Include days into next month
      };
    } else {
      // For week/day/list views, use selected date
      return {
        start: startOfWeek(selectedDate, { weekStartsOn: 1 }),
        end: endOfWeek(selectedDate, { weekStartsOn: 1 })
      };
    }
  }, [viewMode, currentMonth, selectedDate]);

  const { data: slots = [], isLoading: slotsLoading } = useQuery({
    queryKey: ['admin-slots', format(dateRange.start, 'yyyy-MM-dd'), format(dateRange.end, 'yyyy-MM-dd')],
    queryFn: async () => {
      const response = await apiClient.get(`/v1/admin/available_slots?start_date=${format(dateRange.start, 'yyyy-MM-dd')}&end_date=${format(dateRange.end, 'yyyy-MM-dd')}`);
      return response.data.data?.slots || [];
    },
  });

  // Fetch services and providers for form
  const { data: services = [] } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ['providers'],
    queryFn: () => bookingService.getProviders(),
  });

  // Filter to only show active providers in dropdowns for slot creation
  const activeProviders = providers.filter(provider => provider.active);

  // Helper to get provider display name from slot
  const getProviderName = (slot: any) => {
    // Try slot.provider first (API may return name or displayName)
    if (slot.provider?.displayName) return slot.provider.displayName;
    if (slot.provider?.name) return slot.provider.name;

    // Fall back to finding provider by ID from fetched providers list
    const providerId = slot.provider_id || slot.providerId;
    if (providerId) {
      const provider = providers.find(p => p.id === providerId);
      if (provider) return provider.displayName;
    }

    return t('timeSlots:slot.noProvider');
  };

  // Create slot mutation with enhanced error handling
  const createSlotMutation = useMutation({
    mutationFn: async (data: SlotFormData) => {
      setIsSubmitting(true);
      setFieldErrors({});

      // If generateMultiple is enabled, use the generate_from_time_range endpoint
      if (data.generateMultiple && data.endTime) {
        const response = await apiClient.post('/v1/admin/available_slots/generate_from_time_range', {
          provider_id: data.providerId,
          service_id: data.serviceId,
          date: data.date,
          start_time: data.startTime,
          end_time: data.endTime,
          max_capacity: data.maxCapacity || 1
        });

        return response.data;
      }

      // If recurring is enabled, use the generate_recurring endpoint
      if (data.recurring && data.recurringDays && data.recurringDays.length > 0) {
        // Convert day numbers (0=Sunday, 1=Monday...) to day names
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const selectedDayNames = data.recurringDays.map(dayNum => dayNames[dayNum]);

        // Calculate end time from start time and duration
        const startDateTime = new Date(`2000-01-01T${data.startTime}:00`);
        const endDateTime = addMinutes(startDateTime, data.duration);
        const endTimeStr = format(endDateTime, 'HH:mm');

        const response = await apiClient.post('/v1/admin/available_slots/generate_recurring', {
          provider_id: data.providerId,
          service_id: data.serviceId,
          start_date: data.date,
          end_date: data.endDate,
          days_of_week: selectedDayNames,
          time_slots: [{
            start_time: data.startTime,
            end_time: endTimeStr
          }]
        });

        return response.data;
      }

      // Single slot creation
      // Calculate proper end time
      const startDateTime = new Date(`${data.date}T${data.startTime}:00`);
      const endDateTime = addMinutes(startDateTime, data.duration);

      const response = await apiClient.post('/v1/admin/available_slots', {
        provider_id: data.providerId,
        service_id: data.serviceId,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        max_capacity: data.maxCapacity || 1,
        available: true,
        notes: null
      });

      return response.data;
    },
    onSuccess: (result, variables) => {
      // Show different success message for recurring vs single slot
      const slotsCount = result.data?.slots?.length || 1;
      const description = slotsCount > 1
        ? `${slotsCount} recurring slots created successfully`
        : t('timeSlots:toasts.createSuccess');

      toast({
        title: t('common:toasts.success'),
        description: description,
        action: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      });

      setIsCreateDialogOpen(false);
      resetForm();
      setFieldErrors({});
      setConflictingSlots([]);

      // Navigate calendar to the start date of the created slots
      // This will automatically trigger a refetch due to the query key dependency on selectedDate
      if (variables.date) {
        const createdDate = parseISO(variables.date);
        setSelectedDate(createdDate);
        setCurrentMonth(createdDate);
      }

      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-slots'] });
      queryClient.invalidateQueries({ queryKey: ['available-slots'] });
    },
    onError: (error: AxiosError<any>) => {
      console.error('Failed to create slot:', error);

      // Parse backend validation errors (check both paths due to response wrapper)
      const errors = error.response?.data?.errors || error.response?.data?.data?.errors;

      if (errors) {
        setFieldErrors(errors);

        // Set form errors for each field
        Object.keys(errors).forEach((field) => {
          const formField = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
          if (errors[field] && errors[field].length > 0) {
            form.setError(formField as keyof SlotFormData, {
              type: 'server',
              message: errors[field].join(', ')
            });
          }
        });

        toast({
          title: t('timeSlots:toasts.validationError'),
          description: t('timeSlots:toasts.fixErrors'),
          variant: 'destructive',
          action: <XCircle className="h-4 w-4" />,
        });
      } else {
        // Use sanitized error message for all other errors
        toast({
          title: t('common:toasts.error'),
          description: sanitizeErrorMessage(error, t('timeSlots:toasts.createError')),
          variant: 'destructive',
          action: <XCircle className="h-4 w-4" />,
        });
      }
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  // Update slot mutation with enhanced error handling
  const updateSlotMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SlotFormData }) => {
      setIsSubmitting(true);
      setFieldErrors({});

      // Convert form data to API format with proper field names
      const startDateTime = new Date(`${data.date}T${data.startTime}:00`);
      const endDateTime = addMinutes(startDateTime, data.duration);

      const updateData = {
        provider_id: data.providerId,
        service_id: data.serviceId,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        max_capacity: data.maxCapacity || 1,
        available: true,
      };

      return await adminService.updateSlot(id, updateData);
    },
    onSuccess: (result) => {
      toast({
        title: t('common:toasts.success'),
        description: t('timeSlots:toasts.updateSuccess'),
        action: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      });

      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['admin-slots'] });
      queryClient.invalidateQueries({ queryKey: ['available-slots'] });
      queryClient.refetchQueries({ queryKey: ['admin-slots'] });

      setIsEditDialogOpen(false);
      setSelectedSlot(null);
      resetForm();
      setFieldErrors({});
      setConflictingSlots([]);
    },
    onError: (error: AxiosError<any>) => {
      console.error('Failed to update slot:', error);

      // Parse backend validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        setFieldErrors(errors);

        // Set form errors for each field
        Object.keys(errors).forEach((field) => {
          const formField = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
          if (errors[field] && errors[field].length > 0) {
            form.setError(formField as keyof SlotFormData, {
              type: 'server',
              message: errors[field].join(', ')
            });
          }
        });

        toast({
          title: t('timeSlots:toasts.validationError'),
          description: t('timeSlots:toasts.fixErrors'),
          variant: 'destructive',
          action: <XCircle className="h-4 w-4" />,
        });
      } else {
        // Use sanitized error message for all other errors
        toast({
          title: t('common:toasts.error'),
          description: sanitizeErrorMessage(error, t('timeSlots:toasts.updateError')),
          variant: 'destructive',
          action: <XCircle className="h-4 w-4" />,
        });
      }
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  // Delete slot mutation
  const deleteSlotMutation = useMutation({
    mutationFn: async (slotId: number) => {
      const response = await apiClient.delete(`/v1/admin/available_slots/${slotId}`);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: t('common:toasts.success'),
        description: t('timeSlots:toasts.deleteSuccess')
      });
      queryClient.invalidateQueries({ queryKey: ['admin-slots'] });
      queryClient.refetchQueries({ queryKey: ['admin-slots'] });
    },
    onError: (error: any) => {
      console.error('Failed to delete slot:', error);
      toast({
        title: t('common:toasts.error'),
        description: sanitizeErrorMessage(error, t('timeSlots:toasts.deleteError')),
        variant: 'destructive',
      });
    },
  });

  const onSubmit = async (data: SlotFormData) => {
    // Clear previous errors
    setFieldErrors({});

    // Prevent submission if there are conflicts
    if (conflictingSlots.length > 0) {
      toast({
        title: t('timeSlots:toasts.conflictDetected'),
        description: t('timeSlots:toasts.conflictMessage'),
        variant: 'destructive',
        action: <AlertTriangle className="h-4 w-4" />,
      });
      return;
    }

    // Additional client-side validation
    const errors = await form.trigger();
    if (!errors) {
      toast({
        title: t('timeSlots:toasts.validationError'),
        description: t('timeSlots:toasts.fixValidationErrors'),
        variant: 'destructive',
        action: <XCircle className="h-4 w-4" />,
      });
      return;
    }


    // If we're editing, use the update mutation
    if (isEditDialogOpen && selectedSlot) {
      updateSlotMutation.mutate({ id: selectedSlot.id, data });
    } else {
      createSlotMutation.mutate(data);
    }
  };

  const calculateEndTime = (date: string, startTime: string, duration: number) => {
    const startDateTime = new Date(`${date}T${startTime}:00`);
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
    return endDateTime.toISOString();
  };

  const handleServiceChange = (serviceId: number) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service || null);
    form.setValue('serviceId', serviceId);
    if (service) {
      // Always set duration to match service duration_minutes (not including buffer time)
      form.setValue('duration', service.duration_minutes || service.duration || 60);
    }
  };

  const handleEdit = (slot: AvailableSlot) => {
    // API returns snake_case fields, handle both formats
    const startTimeField = slot?.start_time || slot?.startTime;
    const endTimeField = slot?.end_time || slot?.endTime;

    if (!startTimeField || !endTimeField) {
      toast({
        title: t('common:toasts.error'),
        description: t('timeSlots:toasts.cannotEdit'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const startDate = format(parseISO(startTimeField), 'yyyy-MM-dd');
      const startTime = format(parseISO(startTimeField), 'HH:mm');
      const endTime = parseISO(endTimeField);
      const startTimeDate = parseISO(startTimeField);
      const duration = Math.round((endTime.getTime() - startTimeDate.getTime()) / (1000 * 60));

      // Set selected slot first
      setSelectedSlot(slot);

      // Find the corresponding service to set the selected service
      const service = services.find(s => s.id === (slot.service_id || slot.serviceId || slot.service?.id));
      if (service) {
        setSelectedService(service);
      }

      // Reset form with slot data - handle both snake_case (from API) and camelCase
      const formData = {
        providerId: slot.provider_id || slot.providerId || slot.provider?.id || 0,
        serviceId: slot.service_id || slot.serviceId || slot.service?.id || 0,
        date: startDate,
        startTime,
        duration,
        maxCapacity: slot.max_capacity || slot.maxCapacity || 1,
        recurring: false,
        recurringDays: [],
        endDate: '',
      };

      form.reset(formData);

      // Open the edit dialog
      setIsEditDialogOpen(true);
    } catch {
      toast({
        title: t('common:toasts.error'),
        description: t('timeSlots:toasts.parseError'),
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setCreateTab('single');
    setSelectedService(null);
    setSelectedSlot(null);
    setFieldErrors({});
    setConflictingSlots([]);
    setIsCheckingConflict(false);
    setIsSubmitting(false);
    form.clearErrors();
    form.reset({
      providerId: 0,
      serviceId: 0,
      date: format(selectedDate, 'yyyy-MM-dd'),
      startTime: '09:00',
      duration: 60,
      maxCapacity: 1,
      recurring: false,
      recurringDays: [],
      endDate: '',
      generateMultiple: false,
      endTime: '',
    });
  };

  // Helper function to suggest alternative time slots
  const suggestAlternativeSlots = useMemo(() => {
    if (conflictingSlots.length === 0 || !watchedDate || !watchedDuration) return [];

    const suggestions = [];
    const proposedDate = new Date(watchedDate);

    // Find gaps between existing slots
    const daySlots = slots
      .filter((slot: AvailableSlot) => {
        const slotDate = parseISO(slot.start_time || slot.startTime || '');
        return isSameDay(slotDate, proposedDate) &&
               (slot.provider_id === watchedProviderId || slot.providerId === watchedProviderId);
      })
      .sort((a, b) => {
        const aTime = new Date(a.start_time || a.startTime || '').getTime();
        const bTime = new Date(b.start_time || b.startTime || '').getTime();
        return aTime - bTime;
      });

    // Check for available slots before and after conflicts
    for (let hour = 8; hour <= 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const suggestedStart = new Date(proposedDate);
        suggestedStart.setHours(hour, minute, 0, 0);
        const suggestedEnd = addMinutes(suggestedStart, watchedDuration);

        // Check if this time slot is available
        const hasConflict = daySlots.some((slot: AvailableSlot) => {
          const slotStart = new Date(slot.start_time || slot.startTime || '');
          const slotEnd = new Date(slot.end_time || slot.endTime || '');
          return (suggestedStart < slotEnd && suggestedEnd > slotStart);
        });

        if (!hasConflict && suggestions.length < 3) {
          suggestions.push(format(suggestedStart, 'HH:mm'));
        }
      }
    }

    return suggestions;
  }, [conflictingSlots, watchedDate, watchedDuration, watchedProviderId, slots]);

  const handleDelete = (slotId: number) => {
    if (confirm(t('timeSlots:dialogs.deleteConfirm'))) {
      deleteSlotMutation.mutate(slotId);
    }
  };

  const getSlotsForDate = (date: Date) => {
    return slots.filter(slot => {
      try {
        // Handle both snake_case (from API) and camelCase field names
        const startTime = slot?.start_time || slot?.startTime;
        return startTime && isSameDay(parseISO(startTime), date);
      } catch (error) {
        console.error('Error parsing slot date:', error, slot);
        return false;
      }
    });
  };

  // Format time in the selected timezone
  const formatSlotTime = (isoDateString: string) => {
    try {
      const date = parseISO(isoDateString);
      if (displayTimezone === 'local') {
        // Display in browser's local timezone
        return format(date, 'HH:mm');
      } else {
        // Display in selected timezone using Intl API
        const timeZone = displayTimezone === organization?.timezone ? organization.timezone : displayTimezone;
        return new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: timeZone
        }).format(date);
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return '--:--';
    }
  };

  // Map a slot to a CalendarEvent for the unified Calendar component.
  const slotStatus = (slot: AvailableSlot): EventStatus => {
    if (slot.booking_status === 'pending') return 'pending';
    if (!slot.available) return 'confirmed'; // booked
    return 'done'; // open / available
  };

  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const events: CalendarEvent[] = [];
    for (const slot of slots) {
      const startStr = slot.start_time || slot.startTime;
      const endStr = slot.end_time || slot.endTime;
      if (!startStr || !endStr) continue;
      try {
        events.push({
          id: String(slot.id),
          start: parseISO(startStr),
          end: parseISO(endStr),
          title: slot.service?.name || t('timeSlots:slot.noService'),
          subtitle: getProviderName(slot),
          status: slotStatus(slot),
          meta: { slot },
        });
      } catch {
        // skip unparseable rows
      }
    }
    return events;
  }, [slots, t]);

  const handleEventClick = (event: CalendarEvent) => {
    const slot = (event.meta as { slot?: AvailableSlot } | undefined)?.slot;
    if (!slot) return;
    if (!slot.available && slot.booking_id) {
      setLocation(`/admin/bookings/${slot.booking_id}`);
      return;
    }
    handleEdit(slot);
  };

  const handleSlotClick = ({ start }: { start: Date }) => {
    form.reset({
      ...form.getValues(),
      date: format(start, 'yyyy-MM-dd'),
      startTime: format(start, 'HH:mm'),
    });
    setIsCreateDialogOpen(true);
  };

  // Single anchor date used by Calendar — month view tracks its own anchor
  // so the date-range query stays correct when switching views.
  const calendarDate = viewMode === 'month' ? currentMonth : selectedDate;
  const setCalendarDate = (d: Date) => {
    if (viewMode === 'month') setCurrentMonth(d);
    else setSelectedDate(d);
  };

  return (
    <div className="space-y-6">
      {/* Create Slot Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
        setIsCreateDialogOpen(open);
        if (!open) resetForm();
      }}>
            <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-brand-600" />
                  {t('timeSlots:dialogs.createTitle')}
                </DialogTitle>
              </DialogHeader>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Service & Provider Selectors (shared across all tabs) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="serviceId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('timeSlots:form.service')}</FormLabel>
                          <Select
                            value={field.value.toString()}
                            onValueChange={(value) => handleServiceChange(parseInt(value))}
                          >
                            <FormControl>
                              <SelectTrigger className="h-12 bg-white">
                                <SelectValue placeholder={t('timeSlots:form.placeholders.selectService')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {services.length === 0 ? (
                                <div className="p-4 text-center text-sm text-soft">
                                  <p>{t('timeSlots:form.noServices')}</p>
                                  <Button variant="link" asChild className="mt-2 h-auto p-0">
                                    <Link to="/admin/services">{t('timeSlots:form.createServiceFirst')}</Link>
                                  </Button>
                                </div>
                              ) : (
                                services.map((service) => (
                                  <SelectItem key={service.id} value={service.id.toString()}>
                                    <div className="flex flex-col items-start py-1">
                                      <div className="font-medium">{service.name}</div>
                                      <div className="text-sm text-soft">
                                        {formatCurrency(service.price || 0)} • {service.duration_minutes || service.duration || 60}min
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="providerId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('timeSlots:form.provider')}</FormLabel>
                          <Select
                            value={field.value.toString()}
                            onValueChange={(value) => field.onChange(parseInt(value))}
                          >
                            <FormControl>
                              <SelectTrigger className="h-12 bg-white">
                                <SelectValue placeholder={t('timeSlots:form.placeholders.selectProvider')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {activeProviders.length === 0 ? (
                                <div className="p-4 text-center text-sm text-soft">
                                  <p>{t('timeSlots:form.noProviders')}</p>
                                  <Button variant="link" asChild className="mt-2 h-auto p-0">
                                    <Link to="/admin/team?tab=providers">{t('timeSlots:form.addProviderFirst')}</Link>
                                  </Button>
                                </div>
                              ) : (
                                activeProviders.map((provider) => (
                                  <SelectItem key={provider.id} value={provider.id.toString()}>
                                    <div className="flex flex-col items-start py-1">
                                      <div className="font-medium">{provider.displayName}</div>
                                      {provider.specialization && (
                                        <div className="text-sm text-soft">{provider.specialization}</div>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Tabs for slot creation mode */}
                  <Tabs value={createTab} onValueChange={(v) => setCreateTab(v as 'single' | 'recurring' | 'timerange')}>
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="single">{t('timeSlots:tabs.single')}</TabsTrigger>
                      <TabsTrigger value="recurring">{t('timeSlots:tabs.recurring')}</TabsTrigger>
                      <TabsTrigger value="timerange">{t('timeSlots:tabs.timeRange')}</TabsTrigger>
                    </TabsList>

                    {/* Single Slot Tab */}
                    <TabsContent value="single" className="space-y-4 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('timeSlots:form.date')}</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} className="h-12" min={format(new Date(), 'yyyy-MM-dd')} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="startTime"
                          render={({ field }) => {
                            const dateValue = form.watch('date') ? parseISO(form.watch('date')) : new Date();
                            const filteredIntervals = getTimeIntervalsForDate(dateValue);

                            return (
                              <FormItem>
                                <FormLabel>{t('timeSlots:form.startTime')}</FormLabel>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger className="h-12 bg-white">
                                      <SelectValue placeholder={t('timeSlots:form.placeholders.selectTime')} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-60">
                                    {filteredIntervals.map((time) => (
                                      <SelectItem key={time.value} value={time.value}>
                                        {time.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="duration"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('timeSlots:form.duration')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  className="h-12 bg-surface-2"
                                  disabled
                                  readOnly
                                  value={selectedService?.duration_minutes || selectedService?.duration || field.value}
                                />
                              </FormControl>
                              <p className="text-sm text-soft mt-1">
                                {t('timeSlots:form.durationNote')}
                              </p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="maxCapacity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('timeSlots:form.maxCapacity')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={100}
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    const n = parseInt(v, 10);
                                    field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                  }}
                                  onBlur={(e) => {
                                    field.onBlur();
                                    const n = parseInt(e.target.value, 10);
                                    field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 100 ? 100 : n);
                                  }}
                                  className="h-12"
                                  placeholder={t('timeSlots:form.placeholders.enterCapacity')}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Summary Preview */}
                      {selectedService && (
                        <div className="bg-surface-2 p-4 rounded-lg border">
                          <h4 className="font-medium text-ink mb-2">{t('timeSlots:summary.title')}</h4>
                          <div className="text-sm text-soft space-y-1">
                            <div>Service: {selectedService.name}</div>
                            <div>Duration: {selectedService.duration_minutes || selectedService.duration || 60} minutes</div>
                            <div>Date: {form.watch('date') ? format(parseISO(form.watch('date')), 'EEEE, MMMM d, yyyy') : 'Not selected'}</div>
                            <div>Time: {form.watch('startTime')} - {
                              form.watch('startTime') && selectedService
                                ? (() => {
                                    const start = new Date(`2000-01-01T${form.watch('startTime')}:00`);
                                    const duration = selectedService.duration_minutes || selectedService.duration || 60;
                                    const end = new Date(start.getTime() + duration * 60000);
                                    return format(end, 'HH:mm');
                                  })()
                                : '--:--'
                            }</div>
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    {/* Recurring Tab */}
                    <TabsContent value="recurring" className="space-y-4 mt-4">
                      <FormField
                        control={form.control}
                        name="recurringDays"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:recurring.selectDays')}</FormLabel>
                            <div className="flex flex-wrap gap-2">
                              {weekDays.map(({ label, dayId }) => (
                                <Button
                                  key={dayId}
                                  type="button"
                                  variant={field.value?.includes(dayId) ? 'primary' : 'outline'}
                                  size="sm"
                                  onClick={() => {
                                    const currentDays = field.value || [];
                                    const newDays = currentDays.includes(dayId)
                                      ? currentDays.filter(d => d !== dayId)
                                      : [...currentDays, dayId];
                                    field.onChange(newDays);
                                    form.setValue('recurring', true);
                                  }}
                                >
                                  {label}
                                </Button>
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="date"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('timeSlots:form.startDate')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="date"
                                  {...field}
                                  className="h-12"
                                  min={format(new Date(), 'yyyy-MM-dd')}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="endDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('timeSlots:form.endDate')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="date"
                                  {...field}
                                  className="h-12"
                                  min={form.watch('date') || format(new Date(), 'yyyy-MM-dd')}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="startTime"
                          render={({ field }) => {
                            const dateValue = form.watch('date') ? parseISO(form.watch('date')) : new Date();
                            const filteredIntervals = getTimeIntervalsForDate(dateValue);

                            return (
                              <FormItem>
                                <FormLabel>{t('timeSlots:form.startTime')}</FormLabel>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger className="h-12 bg-white">
                                      <SelectValue placeholder={t('timeSlots:form.placeholders.selectTime')} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-60">
                                    {filteredIntervals.map((time) => (
                                      <SelectItem key={time.value} value={time.value}>
                                        {time.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />

                        <FormField
                          control={form.control}
                          name="maxCapacity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('timeSlots:form.maxCapacity')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={100}
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    const n = parseInt(v, 10);
                                    field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                  }}
                                  onBlur={(e) => {
                                    field.onBlur();
                                    const n = parseInt(e.target.value, 10);
                                    field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 100 ? 100 : n);
                                  }}
                                  className="h-12"
                                  placeholder={t('timeSlots:form.placeholders.enterCapacity')}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </TabsContent>

                    {/* Time Range Tab */}
                    <TabsContent value="timerange" className="space-y-4 mt-4">
                      <FormField
                        control={form.control}
                        name="date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:form.date')}</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} className="h-12" min={format(new Date(), 'yyyy-MM-dd')} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="startTime"
                          render={({ field }) => {
                            const dateValue = form.watch('date') ? parseISO(form.watch('date')) : new Date();
                            const filteredIntervals = getTimeIntervalsForDate(dateValue);

                            return (
                              <FormItem>
                                <FormLabel>{t('timeSlots:form.startTime')}</FormLabel>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger className="h-12 bg-white">
                                      <SelectValue placeholder={t('timeSlots:form.placeholders.selectTime')} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-60">
                                    {filteredIntervals.map((time) => (
                                      <SelectItem key={time.value} value={time.value}>
                                        {time.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />

                        <FormField
                          control={form.control}
                          name="endTime"
                          render={({ field }) => {
                            const dateValue = form.watch('date') ? parseISO(form.watch('date')) : new Date();
                            const filteredIntervals = getTimeIntervalsForDate(dateValue);

                            return (
                              <FormItem>
                                <FormLabel>{t('timeSlots:timeRange.endTimeLabel')}</FormLabel>
                                <Select value={field.value} onValueChange={(val) => {
                                  field.onChange(val);
                                  form.setValue('generateMultiple', true);
                                }}>
                                  <FormControl>
                                    <SelectTrigger className="h-12 bg-white">
                                      <SelectValue placeholder={t('timeSlots:form.placeholders.selectTime')} />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-60">
                                    {filteredIntervals.map((time) => (
                                      <SelectItem key={time.value} value={time.value}>
                                        {time.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="maxCapacity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:form.maxCapacity')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={100}
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const n = parseInt(v, 10);
                                  field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                }}
                                onBlur={(e) => {
                                  field.onBlur();
                                  const n = parseInt(e.target.value, 10);
                                  field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 100 ? 100 : n);
                                }}
                                className="h-12"
                                placeholder={t('timeSlots:form.placeholders.enterCapacity')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Preview */}
                      {form.watch('startTime') && form.watch('endTime') && (() => {
                        const startTime = form.watch('startTime');
                        const endTime = form.watch('endTime');
                        const duration = selectedService?.duration_minutes || selectedService?.duration || 60;

                        const [startHour, startMin] = startTime.split(':').map(Number);
                        const [endHour, endMin] = endTime.split(':').map(Number);
                        const startMinutes = startHour * 60 + startMin;
                        const endMinutes = endHour * 60 + endMin;
                        const totalMinutes = endMinutes - startMinutes;
                        const slotCount = totalMinutes > 0 ? Math.floor(totalMinutes / duration) : 0;

                        return (
                          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-ink">{t('timeSlots:labels.preview')}</p>
                                <p className="text-xs text-soft mt-1">
                                  {startTime} to {endTime} ({duration} min slots)
                                </p>
                              </div>
                              <div className="text-right">
                                {slotCount > 0 ? (
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                    <div>
                                      <p className="text-2xl font-bold text-brand-600">{slotCount}</p>
                                      <p className="text-xs text-soft">
                                        {t('timeSlots:timeRange.preview', { count: slotCount })}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-red-600">
                                    <XCircle className="h-5 w-5" />
                                    <p className="text-sm font-medium">{t('timeSlots:timeRange.invalidRange')}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </TabsContent>
                  </Tabs>

                  {/* Footer Buttons */}
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCreateDialogOpen(false)}
                    >
                      {t('common:actions.cancel')}
                    </Button>
                    <Button type="submit" disabled={createSlotMutation.isPending}>
                      {createSlotMutation.isPending ? t('timeSlots:buttons.creating') : t('timeSlots:buttons.createSlot')}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>

          {/* Edit Slot Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            if (!open) {
              setSelectedSlot(null);
              resetForm();
            }
          }}>
            <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <Edit2 className="h-5 w-5 mr-2 text-brand-600" />
                  {t('timeSlots:dialogs.editTitle')}
                </DialogTitle>
              </DialogHeader>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="serviceId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:form.service')}</FormLabel>
                            <Select
                              value={field.value.toString()}
                              onValueChange={(value) => handleServiceChange(parseInt(value))}
                            >
                              <FormControl>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder={t('timeSlots:form.placeholders.selectService')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {services.map((service) => (
                                  <SelectItem key={service.id} value={service.id.toString()}>
                                    <div className="flex flex-col items-start py-1">
                                      <div className="font-medium">{service.name}</div>
                                      <div className="text-sm text-soft">
                                        {formatCurrency(service.price || 0)} • {service.duration_minutes || service.duration || 60}min
                                      </div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="providerId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:form.provider')}</FormLabel>
                            <Select
                              value={field.value.toString()}
                              onValueChange={(value) => field.onChange(parseInt(value))}
                            >
                              <FormControl>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder={t('timeSlots:form.placeholders.selectProvider')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {activeProviders.map((provider) => (
                                  <SelectItem key={provider.id} value={provider.id.toString()}>
                                    <div className="flex flex-col items-start py-1">
                                      <div className="font-medium">{provider.displayName}</div>
                                      {provider.specialization && (
                                        <div className="text-sm text-soft">{provider.specialization}</div>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:form.date')}</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                {...field}
                                className="h-12"
                                min={format(new Date(), 'yyyy-MM-dd')}
                              />
                            </FormControl>
                            <FormMessage />
                            {fieldErrors.date?.map((error, idx) => (
                              <p key={idx} className="text-sm text-red-600 mt-1">{error}</p>
                            ))}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="startTime"
                        render={({ field }) => {
                          const selectedDate = form.watch('date') ? parseISO(form.watch('date')) : new Date();
                          const filteredIntervals = getTimeIntervalsForDate(selectedDate);

                          return (
                            <FormItem>
                              <FormLabel>{t('timeSlots:form.startTime')}</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger className="h-12 bg-white">
                                    <SelectValue placeholder={t('timeSlots:form.placeholders.selectTime')} />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="max-h-60">
                                  {filteredIntervals.map((time) => (
                                    <SelectItem key={time.value} value={time.value}>
                                      {time.label}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        );
                        }}
                      />

                      <FormField
                        control={form.control}
                        name="duration"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:form.duration')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                {...field}
                                className="h-12 bg-surface-2"
                                disabled
                                readOnly
                                value={selectedService?.duration_minutes || selectedService?.duration || field.value}
                              />
                            </FormControl>
                            <p className="text-sm text-soft mt-1">
                              {t('timeSlots:form.durationNote')}
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                      <FormField
                        control={form.control}
                        name="maxCapacity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('timeSlots:form.maxCapacity')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={100}
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const n = parseInt(v, 10);
                                  field.onChange(v === '' || Number.isNaN(n) ? null : n);
                                }}
                                onBlur={(e) => {
                                  field.onBlur();
                                  const n = parseInt(e.target.value, 10);
                                  field.onChange(Number.isNaN(n) || n < 1 ? 1 : n > 100 ? 100 : n);
                                }}
                                className="h-12"
                                placeholder={t('timeSlots:form.placeholders.enterCapacity')}
                              />
                            </FormControl>
                            <p className="text-sm text-soft mt-1">
                              {t('timeSlots:form.capacityNote')}
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {selectedService && (
                      <div className="bg-brand-50 p-4 rounded-lg border border-brand-200">
                        <h4 className="font-medium text-brand-900 mb-2">{t('timeSlots:summary.serviceDetails')}</h4>
                        <div className="text-sm text-brand-700 space-y-1">
                          <div>Service: {selectedService.name}</div>
                          <div>Duration: {selectedService.duration_minutes || selectedService.duration || 60} minutes</div>
                          <div>Price: {formatCurrency(selectedService.price || 0)}</div>
                          {selectedService.description && (
                            <div>Description: {selectedService.description}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Conflict Warning for Edit Dialog */}
                    {isCheckingConflict && (
                      <Alert className="border-yellow-200 bg-yellow-50">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <AlertDescription>
                          {t('timeSlots:conflicts.checking')}
                        </AlertDescription>
                      </Alert>
                    )}

                    {conflictingSlots.length > 0 && !isCheckingConflict && (
                      <Alert className="border-red-200 bg-red-50">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <AlertDescription>
                          <p className="font-medium text-red-900 mb-2">
                            {t('timeSlots:conflicts.detected')}
                          </p>
                          <p className="text-sm text-red-700">
                            {t('timeSlots:conflicts.message')}
                          </p>
                          {suggestAlternativeSlots.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-red-200">
                              <p className="text-sm font-medium text-red-900 mb-1">
                                {t('timeSlots:conflicts.availableTimes')}
                              </p>
                              <div className="flex gap-2">
                                {suggestAlternativeSlots.map((time) => (
                                  <button
                                    key={time}
                                    type="button"
                                    className="px-2 py-1 text-xs bg-white border border-red-200 rounded hover:bg-red-50 text-red-700"
                                    onClick={() => form.setValue('startTime', time)}
                                  >
                                    {time}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="bg-surface-2 p-4 rounded-lg border">
                      <h4 className="font-medium text-ink mb-2">{t('timeSlots:summary.slotSummary')}</h4>
                      <div className="text-sm text-soft space-y-1">
                        <div>Date: {form.watch('date') ? format(new Date(form.watch('date')), 'EEEE, MMMM d, yyyy') : 'Not set'}</div>
                        <div>Time: {form.watch('startTime')} - {
                          form.watch('startTime') && form.watch('duration')
                            ? (() => {
                                const start = new Date(`2000-01-01T${form.watch('startTime')}:00`);
                                const end = new Date(start.getTime() + form.watch('duration') * 60000);
                                return format(end, 'HH:mm');
                              })()
                            : '--:--'
                        }</div>
                        <div>Duration: {form.watch('duration')} minutes</div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditDialogOpen(false);
                          setSelectedSlot(null);
                          resetForm();
                        }}
                      >
                        {t('common:actions.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || conflictingSlots.length > 0 || !form.formState.isValid}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('timeSlots:buttons.updating')}
                          </>
                        ) : conflictingSlots.length > 0 ? (
                          t('timeSlots:buttons.resolveConflicts')
                        ) : (
                          t('timeSlots:buttons.updateSlot')
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>

      <div className="rounded-xl border border-line bg-surface p-4">
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as typeof viewMode)}>
          <TabsList aria-label={t('timeSlots:viewModes.ariaLabel') || 'View'}>
            <TabsTrigger value="day">{t('timeSlots:viewModes.day')}</TabsTrigger>
            <TabsTrigger value="week">{t('timeSlots:viewModes.week')}</TabsTrigger>
            <TabsTrigger value="month">{t('timeSlots:viewModes.month')}</TabsTrigger>
            <TabsTrigger value="list">{t('timeSlots:viewModes.list')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {viewMode === 'list' ? (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {slotsLoading ? (
              <div className="px-4 py-12 text-center text-soft">{t('timeSlots:loading')}</div>
            ) : slots.length === 0 ? (
              <div className="px-4 py-12 text-center text-soft">
                <CalendarIcon className="h-10 w-10 mx-auto mb-3 text-line-strong" />
                <p>{t('timeSlots:empty.noSlotsFound')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {[...slots]
                  .sort((a, b) => {
                    const aTime = new Date(a.start_time || a.startTime || '').getTime();
                    const bTime = new Date(b.start_time || b.startTime || '').getTime();
                    return aTime - bTime;
                  })
                  .map((slot) => {
                    const startStr = slot.start_time || slot.startTime;
                    const endStr = slot.end_time || slot.endTime;
                    const isBooked = !slot.available;
                    const isPending = isBooked && slot.booking_status === 'pending';
                    const status = isPending ? 'pending' : isBooked ? 'confirmed' : 'completed';
                    return (
                      <li
                        key={slot.id}
                        onClick={() => {
                          if (isBooked && slot.booking_id) {
                            setLocation(`/admin/bookings/${slot.booking_id}`);
                          }
                        }}
                        className={`flex items-center justify-between gap-4 px-4 py-3 hover:bg-surface-2/60 transition-colors ${
                          isBooked && slot.booking_id ? 'cursor-pointer' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Clock className="h-5 w-5 text-soft shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-ink truncate" data-numeric>
                              {startStr ? format(parseISO(startStr), 'EEE, MMM d') : '—'} ·{' '}
                              {formatSlotTime(startStr || '')}–{formatSlotTime(endStr || '')}
                            </div>
                            <div className="text-body-xs text-soft truncate">
                              {slot.service?.name || t('timeSlots:slot.noService')} · {getProviderName(slot)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusBadge status={status}>
                            {isPending
                              ? t('bookings:status.pending')
                              : isBooked
                                ? t('timeSlots:slot.booked')
                                : t('timeSlots:slot.available')}
                          </StatusBadge>
                          {(slot.max_capacity || slot.maxCapacity || 1) > 1 && (
                            <StatusBadge status="completed">
                              {t('capacity.bookedStatus', {
                                current: slot.current_bookings || slot.currentBookings || 0,
                                max: slot.max_capacity || slot.maxCapacity || 1,
                                ns: 'timeSlots',
                              })}
                            </StatusBadge>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(slot);
                            }}
                            aria-label={t('common:actions.edit')}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2 text-danger border-danger/40 hover:bg-danger-soft"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(slot.id);
                            }}
                            aria-label={t('common:actions.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : (
        <Calendar
          view={viewMode as CalendarView}
          date={calendarDate}
          onDateChange={setCalendarDate}
          events={calendarEvents}
          loading={slotsLoading}
          hideViewTabs
          onEventClick={handleEventClick}
          onSlotClick={handleSlotClick}
        />
      )}

    </div>
  );
}
